import React, { useEffect, useState, useRef } from "react";
import { Snackbar, Alert } from "@mui/material";
import { getCurrentUser } from "../services/pocketbase";

const NeuromancerMode = () => {
  const [active, setActive] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [roleAssigned, setRoleAssigned] = useState(false);
  const [roleError, setRoleError] = useState(null);
  const canvasRef = useRef(null);
  const connectionsRef = useRef([]);
  const animationFrameRef = useRef(null);
  const user = getCurrentUser();

  useEffect(() => {
    // Handler for keyboard shortcut (Ctrl+Alt+N)
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.altKey && event.key === "n") {
        event.preventDefault();
        setActive((prev) => !prev);
        setShowTooltip(true);

        // Only attempt to assign role if we have a Discord ID and role hasn't been assigned yet
        if (user?.discord_id && !roleAssigned) {
          assignDiscordRole();
        }

        // Show tooltip for 3 seconds
        setTimeout(() => setShowTooltip(false), 3000);
      }
    };

    // Add event listener
    window.addEventListener("keydown", handleKeyDown);

    // Apply or remove effects when active state changes
    if (active) {
      applyNeuromancerEffects();
    } else {
      removeNeuromancerEffects();
    }

    // Clean up
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      removeNeuromancerEffects();
    }; // eslint-disable-next-line
  }, [active, roleAssigned, user]);

  // Function to assign Discord role
  const assignDiscordRole = async () => {
    try {
      setRoleError(null);

      const response = await fetch("/api/discord/assign-role", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          discordId: user.discord_id,
          roleId: "1374799806731649166", // Replace with actual Neuromancer role ID
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.details || data.error || "Failed to assign role");
      }

      setRoleAssigned(true);
    } catch (err) {
      console.error("Error assigning Neuromancer role:", err);
      setRoleError(err.message || "Failed to assign Discord role");
    }
  };

  // Apply the Neuromancer effects
  const applyNeuromancerEffects = () => {
    // 1. Add CRT filter overlay
    const crtOverlay = document.createElement("div");
    crtOverlay.id = "neuromancer-crt-overlay";
    document.body.appendChild(crtOverlay);

    // 2. Create WebGL canvas for neural connections
    const canvas = document.createElement("canvas");
    canvas.id = "neuromancer-canvas";
    document.body.appendChild(canvas);
    canvasRef.current = canvas;

    // 3. Add CSS for cyberpunk effects
    const style = document.createElement("style");
    style.id = "neuromancer-style";
    style.innerHTML = `
      #neuromancer-crt-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 224, 255, 0.1) 50%), 
                    linear-gradient(90deg, rgba(255, 0, 0, 0.03), rgba(0, 255, 0, 0.02), rgba(0, 0, 255, 0.03));
        background-size: 100% 2px, 3px 100%;
        pointer-events: none;
        z-index: 9999;
      }
      
      #neuromancer-crt-overlay::before {
        content: "";
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: radial-gradient(circle at center, transparent 60%, rgba(0, 224, 255, 0.2) 100%);
        pointer-events: none;
      }
      
      #neuromancer-canvas {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 9998;
      }
      
      body.neuromancer-active {
        filter: saturate(1.2) contrast(1.1);
      }
      
      body.neuromancer-active button,
      body.neuromancer-active a,
      body.neuromancer-active input,
      body.neuromancer-active .MuiPaper-root {
        text-shadow: 0 0 5px #00e0ff;
        box-shadow: 0 0 10px rgba(0, 224, 255, 0.5);
        transition: all 0.3s ease;
      }
      
      body.neuromancer-active button:hover,
      body.neuromancer-active a:hover {
        text-shadow: 0 0 8px #00e0ff, 0 0 12px #00e0ff;
        box-shadow: 0 0 15px rgba(0, 224, 255, 0.8);
      }
    `;
    document.head.appendChild(style);
    document.body.classList.add("neuromancer-active");

    // Initialize WebGL neural connections
    initNeuralConnections();
  };

  // Remove all Neuromancer effects
  const removeNeuromancerEffects = () => {
    // Remove CRT overlay
    const overlay = document.getElementById("neuromancer-crt-overlay");
    if (overlay) {
      document.body.removeChild(overlay);
    }

    // Remove WebGL canvas
    const canvas = document.getElementById("neuromancer-canvas");
    if (canvas) {
      document.body.removeChild(canvas);
    }

    // Remove CSS
    const style = document.getElementById("neuromancer-style");
    if (style) {
      document.head.removeChild(style);
    }
    document.body.classList.remove("neuromancer-active");

    // Stop animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  // Initialize WebGL neural connections
  const initNeuralConnections = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas size to match window
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Get WebGL context
    const gl = canvas.getContext("webgl");
    if (!gl) {
      console.error("WebGL not supported");
      return;
    }

    // Find interactive elements to connect
    const elements = document.querySelectorAll(
      "button, a, input, .MuiPaper-root"
    );
    const nodes = [];

    elements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        nodes.push({
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
          width: rect.width,
          height: rect.height,
          element: el,
        });
      }
    });

    // Create connections between nodes
    connectionsRef.current = [];
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        // Only connect nodes that are relatively close
        const dx = nodes[i].x - nodes[j].x;
        const dy = nodes[i].y - nodes[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 300) {
          // Only connect elements within 300px
          connectionsRef.current.push({
            from: nodes[i],
            to: nodes[j],
            pulseOffset: Math.random() * 2 * Math.PI, // Random phase offset
            pulseSpeed: 0.5 + Math.random() * 1.5, // Random speed
          });
        }
      }
    }

    // Set up WebGL
    const vertexShader = gl.createShader(gl.VERTEX_SHADER);
    gl.shaderSource(
      vertexShader,
      `
      attribute vec2 position;
      void main() {
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `
    );
    gl.compileShader(vertexShader);

    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
    gl.shaderSource(
      fragmentShader,
      `
      precision mediump float;
      uniform vec4 color;
      void main() {
        gl_FragColor = color;
      }
    `
    );
    gl.compileShader(fragmentShader);

    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.useProgram(program);

    const positionLocation = gl.getAttribLocation(program, "position");
    const colorLocation = gl.getUniformLocation(program, "color");
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    // Animation function
    const animate = (time) => {
      // Resize canvas if window size changed
      if (
        canvas.width !== window.innerWidth ||
        canvas.height !== window.innerHeight
      ) {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        gl.viewport(0, 0, canvas.width, canvas.height);
      }

      // Clear canvas
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);

      // Update and draw connections
      connectionsRef.current.forEach((connection) => {
        // Convert screen coordinates to WebGL coordinates (-1 to 1)
        const x1 = (connection.from.x / canvas.width) * 2 - 1;
        const y1 = -((connection.from.y / canvas.height) * 2 - 1);
        const x2 = (connection.to.x / canvas.width) * 2 - 1;
        const y2 = -((connection.to.y / canvas.height) * 2 - 1);

        // Calculate pulse effect
        const pulse =
          Math.sin(
            time * 0.001 * connection.pulseSpeed + connection.pulseOffset
          ) *
            0.5 +
          0.5;

        // Set color with pulse effect
        gl.uniform4f(colorLocation, 0, 0.8 * pulse, 1 * pulse, 0.3 * pulse);

        // Draw line
        gl.bufferData(
          gl.ARRAY_BUFFER,
          new Float32Array([x1, y1, x2, y2]),
          gl.STATIC_DRAW
        );
        gl.drawArrays(gl.LINES, 0, 2);
      });

      // Continue animation
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Start animation
    animationFrameRef.current = requestAnimationFrame(animate);

    // Handle window resize
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  };

  return (
    <>
      <Snackbar
        open={showTooltip}
        autoHideDuration={3000}
        onClose={() => setShowTooltip(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={active ? "success" : "info"}
          sx={{
            width: "100%",
            backgroundColor: active ? "rgba(0, 224, 255, 0.2)" : undefined,
            color: active ? "#00e0ff" : undefined,
            border: active ? "1px solid #00e0ff" : undefined,
            boxShadow: active ? "0 0 10px rgba(0, 224, 255, 0.5)" : undefined,
          }}
        >
          {active
            ? "Neuromancer Mode activated. Welcome to cyberspace, cowboy."
            : "Neuromancer Mode deactivated. Back to reality."}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!roleError}
        autoHideDuration={5000}
        onClose={() => setRoleError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          {roleError}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NeuromancerMode;
